import { inject, Injectable } from '@angular/core';
import { addMonths, endOfDay, startOfMonth } from 'date-fns';
import { Subject, take } from 'rxjs';
import { AuthService } from '../auth/auth.service'; // Use alias
import { Appointment } from '../models';
import { AppointmentService } from '../services/appointment.service'; // Use alias
import { GoogleCalendarService } from '../services/google-calendar.service'; // Use alias

@Injectable({
  providedIn: 'root'
})
export class AutosyncCalendarService {
  viewDate: Date = new Date();
  private googleCalendarService = inject(GoogleCalendarService);
  private appointmentService = inject(AppointmentService);
  private authService = inject(AuthService);
  private syncing = false;
  private destroy$ = new Subject<void>();

  constructor() {
  }

  async autoSync() {
    if (this.syncing) {
      console.warn('Sync already in progress. Aborting.');
      return;
    }

    if (!this.googleCalendarService.getAutoSyncSetting()) {
      console.log('Auto-sync is disabled. Aborting.');
      return;
    }

    if (!(await this.googleCalendarService.isSignedIn().pipe(take(1)).toPromise())) {
      console.warn('User not signed in to Google Calendar. Aborting auto-sync.');
      return;
    }

    this.syncing = true;
    console.log('Starting auto-sync of appointments with Google Calendar...');

    const startDate = startOfMonth(this.viewDate);
    const endDate = endOfDay(addMonths(this.viewDate, 3));

    this.appointmentService.getAppointmentsForDates(startDate, endDate)
      .subscribe(
        (fetchedAppointments: Appointment[]) => {
          this.syncAppointmentsWithGoogle(fetchedAppointments);
        },
        (error: any) => {
          console.error('Error fetching appointments for auto-sync: ', error);
          this.syncing = false;
        }
      );

    // TODO: Implement blocked slots fetching and syncing
  }

  public async syncAppointmentsWithGoogle(appointments: any[]) {
    try {
      for (const appt of appointments) {
        const googleEvent = this.mapAppointmentToGoogleEvent(appt);
        const googleEventId = await this.googleCalendarService.createOrUpdateEvent(googleEvent);

        if (googleEventId && !appt.googleEventId) {
          await this.appointmentService.updateAppointmentWithGoogleEventId(appt.id, googleEventId);
          console.log(`Created new Google Calendar event for appointment: ${appt.id}, Google Event ID: ${googleEventId}`);
        } else if (appt.googleEventId) {
          console.log(`Updated Google Calendar event for appointment: ${appt.id}`);
        }
      }
      console.log('All appointments synced successfully with Google Calendar.');
    } catch (error) {
      console.error('Error during syncing appointments with Google Calendar: ', error);
    } finally {
      this.syncing = false;
    }
  }

  private mapAppointmentToGoogleEvent(appointment: any): any {
    const start = appointment?.start;
    const durationInMinutes = appointment?.duration || 60;
    let end = appointment?.end; // Use let for end

    if (!start || !(start instanceof Date)) {
      console.error('Invalid start date for appointment:', appointment);
      throw new Error('Appointment start date is invalid.');
    }

    // If end is null or not a Date object, calculate it from start and duration
    if (!end || !(end instanceof Date)) {
      end = new Date(start.getTime() + durationInMinutes * 60000);
    }

    const mapUrl = this.getLocationUrl(appointment?.facilityDetails);
    const notes = `Facility Name: ${appointment?.facilityDetails?.name || ''}
Details: ${appointment?.details || ''}
Type: ${appointment?.type || ''}
Notes: ${appointment?.notes || 'No notes added'}`;

    return {
      summary: `${appointment.name} - ${appointment.orgName}`, // Title of the event
      start: {
        dateTime: start.toISOString(),
        timeZone: 'America/Los_Angeles', // TODO: Make configurable
      },
      end: {
        dateTime: end.toISOString(),
        timeZone: 'America/Los_Angeles', // TODO: Make configurable
      },
      description: notes,
      location: mapUrl,
      // Store original appointment ID and potentially Google Event ID for updates
      meta: { id: appointment?.id, googleEventId: appointment?.googleEventId },
    };
  }

  private getLocationUrl(facility: any): string {
    if (!facility || !facility.address || !facility.city || !facility.state || !facility.zip) {
      return '';
    }
    const address = `${facility.address.trim()},${facility.city.trim()},${facility.state.trim()},${facility.zip.trim()}`;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  async syncAppointmentToGoogle(appointment: any): Promise<void> {
    if (!(await this.googleCalendarService.isSignedIn().pipe(take(1)).toPromise())) {
      console.warn('User not signed in to Google Calendar. Aborting single appointment sync.');
      return;
    }
    try {
      const googleEventId = await this.googleCalendarService.createOrUpdateEvent(this.mapAppointmentToGoogleEvent(appointment));
      if (googleEventId && !appointment.googleEventId) {
        // Only update if it's a new event being created in Google Calendar
        await this.appointmentService.updateAppointmentWithGoogleEventId(appointment.id, googleEventId);
      }
      console.log(`Single appointment ${appointment.id} synced to Google Calendar.`);
    } catch (error) {
      console.error(`Error syncing single appointment ${appointment.id} to Google Calendar:`, error);
    }
  }

  async deleteAppointmentFromGoogle(appointment: any): Promise<void> {
    if (!(await this.googleCalendarService.isSignedIn().pipe(take(1)).toPromise())) {
      console.warn('User not signed in to Google Calendar. Aborting single appointment delete.');
      return;
    }
    if (!appointment.googleEventId) {
      console.warn(`Appointment ${appointment.id} has no Google Event ID. Skipping deletion from Google Calendar.`);
      return;
    }
    try {
      await this.googleCalendarService.deleteEvent(appointment.googleEventId);
      console.log(`Single appointment ${appointment.id} deleted from Google Calendar.`);
    } catch (error) {
      console.error(`Error deleting single appointment ${appointment.id} from Google Calendar:`, error);
    }
  }


}
